import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Tag, message } from "antd";
import { format } from "date-fns";
import { getSaleReturnById, getSaleReturns } from "../../apis/saleReturns";
import { useNotification } from "../../NotificationContext";
import CommonPagination from "../../components/CommanPagination";

const refundModeLabel = (mode?: string) => {
  switch (mode) {
    case "cash":
      return "Cash";
    case "credit":
      return "Credit";
    case "adjust_against_next_bill":
      return "Adjust against next bill";
    default:
      return mode || "-";
  }
};

const CreditNoteList: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [rows, setRows] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  useEffect(() => {
    fetchRows({ page: 1, per_page: 10 });
  }, []);

  const fetchRows = (params: Record<string, any> = {}) => {
    setLoading(true);
    getSaleReturns({ per_page: 10, ...params })
      .then((res: any) => {
        const data = res.data?.data || res.data || [];
        const metaData = res.meta || res.data?.meta || {};
        setRows(Array.isArray(data) ? data : []);
        setMeta(metaData);
      })
      .catch(() => {
        showNotification("error", "Failed to fetch credit notes", "", "topRight");
      })
      .finally(() => setLoading(false));
  };

  const handleView = (id: number) => {
    getSaleReturnById(id)
      .then((res: any) => {
        setSelected(res.data?.data || res.data || res);
        setIsViewOpen(true);
      })
      .catch(() => message.error("Failed to fetch credit note details"));
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    fetchRows({ page, per_page: pageSize || 10 });
  };

  const formatCurrency = (amount: number | string | undefined) =>
    `₹${(Number(amount) || 0).toFixed(2)}`;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">Credit Notes</h1>
          <p className="mt-1 text-sm text-gray-500">
            Pharmacy sale returns and credit notes. Create a credit note from a sale in POS Sales.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/pharmacy/pos/list")}
          className="mt-4 sm:mt-0 px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Create from Sale
        </button>
      </div>

      <div className="flow-root mt-8">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Credit Note
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Sale</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Patient</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Items</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Refund</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-500">
                        No credit notes found
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="py-4 pr-3 pl-4 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">
                            {row.creditNoteNo || row.returnNumber || `#${row.id}`}
                          </div>
                          <div className="text-xs text-gray-400">
                            {row.returnDate
                              ? format(new Date(row.returnDate), "dd MMM yyyy, h:mm a")
                              : "-"}
                          </div>
                          {row.isGstAdjustable ? (
                            <Tag color="green" className="mt-1">
                              GST adjustable
                            </Tag>
                          ) : null}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {row.saleNumber || `#${row.saleId}`}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {row.patient
                            ? `${row.patient.firstName || ""} ${row.patient.lastName || ""}`.trim() ||
                              "Walk-in"
                            : "Walk-in"}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {row.itemsCount ?? 0} / qty {row.totalQuantity ?? 0}
                        </td>
                        <td className="px-3 py-4 text-sm font-semibold text-gray-900">
                          {formatCurrency(row.totalAmount)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {refundModeLabel(row.refundMode)}
                        </td>
                        <td className="px-3 py-4 text-sm">
                          <Button size="small" onClick={() => handleView(row.id)}>
                            View
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>

      <Modal
        title="Credit Note Details"
        open={isViewOpen}
        onCancel={() => {
          setIsViewOpen(false);
          setSelected(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsViewOpen(false);
              setSelected(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-700">Return Number</p>
                <p className="text-sm text-gray-900">{selected.returnNumber || `-`}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Credit Note No</p>
                <p className="text-sm text-gray-900">{selected.creditNoteNo || "-"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Sale</p>
                <p className="text-sm text-gray-900">
                  {selected.sale?.saleNumber || selected.saleNumber || `#${selected.saleId}`}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Refund Mode</p>
                <p className="text-sm text-gray-900">{refundModeLabel(selected.refundMode)}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Patient</p>
                <p className="text-sm text-gray-900">
                  {selected.patient
                    ? `${selected.patient.firstName || ""} ${selected.patient.lastName || ""}`.trim() ||
                      "Walk-in"
                    : "Walk-in"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Total</p>
                <p className="text-sm text-gray-900">{formatCurrency(selected.totalAmount)}</p>
              </div>
            </div>

            {selected.reason ? (
              <div>
                <p className="text-sm font-semibold text-gray-700">Reason</p>
                <p className="text-sm text-gray-900">{selected.reason}</p>
              </div>
            ) : null}

            <div>
              <p className="mb-2 text-sm font-semibold text-gray-700">Returned Items</p>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-left">Batch</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Rate</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Restock</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(selected.items || []).map((item: any) => (
                    <tr key={item.id}>
                      <td className="px-3 py-2">
                        {item.pharmacyItem?.name || "-"}
                        <div className="text-xs text-gray-400">{item.pharmacyItem?.sku}</div>
                      </td>
                      <td className="px-3 py-2">{item.batchNo || item.batch?.batchNumber || "-"}</td>
                      <td className="px-3 py-2 text-right">{item.qtyReturned}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(item.rate)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(item.amount)}</td>
                      <td className="px-3 py-2">
                        <Tag color={item.restockFlag ? "green" : "orange"}>
                          {item.restockFlag ? "Yes" : "No"}
                        </Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CreditNoteList;
